import type { ChangeEventHandler } from 'react';
import React, { useCallback, useMemo } from 'react';

import type { StripeCardElementChangeEvent } from '@stripe/stripe-js';

import { useI18n } from '@change-corgi/core/react/i18n';
import { Box } from '@change-corgi/design-system-legacy/layout';

import { DEFAULT_ERROR_VALIDATION, stripeErrorI18nKey, stripeInputName } from 'src/app/shared/utils/payments';

import { CardElementWrapper } from '../../../../components/StripeElementWrappers';
import { Input } from '../../../../components/StripeElementWrappers/Input';
import type { StripeCreditCardFormWithInternalProps } from '../../types';

const sunriseInputStyles = {
	borderColor: 'sunrise.semantic.stroke.weaker',
	// eslint-disable-next-line @typescript-eslint/naming-convention
	'&[data-error=true]': {
		borderColor: 'sunrise.semantic.stroke.error.weak',
		borderWidth: 1,
		boxShadow: 'none',
	},
	// eslint-disable-next-line @typescript-eslint/naming-convention
	'&:hover': {
		borderColor: 'sunrise.semantic.stroke.accent.primary.strong',
		boxShadow: 'none',
		cursor: 'pointer',
	},
	// eslint-disable-next-line @typescript-eslint/naming-convention
	'&:hover::placeholder': {
		color: 'sunrise.semantic.typography.strong',
	},
	// eslint-disable-next-line @typescript-eslint/naming-convention
	'&:has(.StripeElement--focus):not(:has(.StripeElement--invalid))': {
		borderColor: 'sunrise.semantic.stroke.accent.primary.strong',
		borderWidth: 2,
		boxShadow: 'none',
	},
};

export function SingleForm(props: StripeCreditCardFormWithInternalProps): React.JSX.Element {
	const { translate } = useI18n();
	const { isSunriseEnabled, labelWeight } = props;

	const { onCardDetailsFieldFocus, validationErrors, setValidationErrors } = props;

	const onChange = useCallback(
		(e: StripeCardElementChangeEvent) => {
			setValidationErrors?.({
				...DEFAULT_ERROR_VALIDATION,
				[stripeInputName(e.elementType) as string]: e.error ? stripeErrorI18nKey(e.error) : null,
			});
		},
		[setValidationErrors],
	);

	const error = useMemo(() => {
		if (validationErrors.card) {
			return validationErrors.card;
		}
		if (validationErrors.card_number) {
			return validationErrors.card_number;
		}
		if (validationErrors.card_expiration) {
			return validationErrors.card_expiration;
		}
		if (validationErrors.card_cvc) {
			return validationErrors.card_cvc;
		}
		return null;
	}, [validationErrors]);

	const labelText = translate('corgi.co.promote.card_details');

	const label = labelWeight === 'bold' ? { label: labelText } : { helperText: labelText };

	return (
		<Box
			data-testid="credit-card-number"
			sx={{
				position: 'relative',
				WebkitTransform: 'translate3d(0, 0, 0)',
			}}
		>
			<Input
				className="iframe-form-element" // TODO: this is here for the regression-qaa tests. Migrate the tests to use the data-qa attribute.
				data-qa="iframe-form-element"
				error={error}
				{...label}
				name="card-details"
				as={CardElementWrapper}
				onFocus={onCardDetailsFieldFocus}
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				onChange={onChange as unknown as ChangeEventHandler<HTMLInputElement>}
				id="card-details-input"
				sx={{
					height: 40,
					display: 'flex',
					alignItems: 'center',
					// eslint-disable-next-line @typescript-eslint/naming-convention
					'.StripeElement': {
						width: '100%',
					},
					...(isSunriseEnabled && sunriseInputStyles),
				}}
				isSunriseEnabled={isSunriseEnabled}
			/>
		</Box>
	);
}
